/**
 * useClinicLocations — the selected clinic's locations, for surfaces that need
 * to LABEL or FILTER by site rather than administer them.
 *
 * Mirrors `useUserDisplayNames`: one fetch per session per clinic, cached 5
 * minutes via TanStack Query, with a resolver that never returns null so render
 * sites stay trivial. `locationName(id)` falls back to the id itself when the
 * site isn't in the map (deleted since the interaction happened, or the map is
 * still loading) — an unresolvable id renders verbatim rather than blank, so
 * the row stays diagnosable.
 *
 * `GET /api/locations` is gated by `gateClientAccess`, not `isAdmin`, so client
 * users can read it — which is what lets Dispatch's location filter work for
 * the clinic staff it was built for.
 *
 * Plan: docs/plans/2026-09-10-dispatch-location-column-and-filter.md.
 */

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useAuth } from '../shell/useAuth';

export interface ClinicLocation {
  locationId: string;
  name: string;
  isPrimary?: boolean;
}

interface LocationsResponse {
  locations: { locationId: string; name: string; isPrimary?: boolean }[];
}

const FIVE_MINUTES = 5 * 60 * 1000;

export interface ClinicLocationsResult {
  locations: ClinicLocation[];
  /** Display name for `id`, or `id` itself when unresolved. Empty string for
   *  null/undefined so callers don't need to null-guard. */
  locationName: (id: string | null | undefined) => string;
  /** True only when the VIEWER genuinely has more than one site to distinguish.
   *  Gates the Dispatch location column + filter — six of ten production
   *  clients are single-site, where a location column is pure noise, and a user
   *  scoped to ONE site is in exactly the same position (migration 0161).
   *  ⚠️ That is also why an out-of-scope filter value must be DROPPED rather
   *  than rejected: with the control hidden, a 403 on a stale bookmark would be
   *  an error the user has no way to clear. */
  isMultiLocation: boolean;
  isLoading: boolean;
}

export function useClinicLocations(): ClinicLocationsResult {
  const { selectedClinic } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['clinic-locations', selectedClinic],
    queryFn: () =>
      apiFetch<LocationsResponse>(
        `/api/locations?clientId=${encodeURIComponent(selectedClinic ?? '')}`,
      ),
    enabled: !!selectedClinic,
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES,
  });

  const locations = useMemo<ClinicLocation[]>(
    () =>
      (data?.locations ?? []).map((l) => ({
        locationId: l.locationId,
        name: l.name,
        isPrimary: l.isPrimary,
      })),
    [data],
  );

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const l of locations) m.set(l.locationId, l.name);
    return m;
  }, [locations]);

  const locationName = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return '';
      return nameById.get(id) ?? id;
    },
    [nameById],
  );

  return {
    locations,
    locationName,
    // `locations` is already narrowed to the viewer's sites by the server, so
    // a scoped user's count IS their own world — no extra term needed. The
    // Unknown bucket does not make a one-site user "multi": it is not a place
    // the column can name.
    isMultiLocation: locations.length > 1,
    isLoading,
  };
}
